import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { setDocumentTitle } from "../hooks/useDocumentTitle";

/**
 * Titles for routes whose name is fixed. Longest match wins, so
 * `/rosters/roster-images` beats `/rosters`.
 *
 * Routes whose title depends on loaded data are deliberately absent. They call
 * `useDocumentTitle` themselves, and this component leaves the title alone for
 * any path it cannot match so it does not overwrite them. Effects in a parent
 * run after effects in its children, so a fallback here would always win.
 */
const STATIC_TITLES: Record<string, string> = {
  "/login": "Sign In",
  "/terms": "Terms of Service",
  "/privacy": "Privacy Policy",
  "/forgot-password": "Forgot Password",
  "/reset-password": "Reset Password",
  "/auth/complete-reset": "Account Email",
  "/accept-invite": "Accept Invitation",
  "/setup-program": "Set Up Your Program",
  "/signup": "Sign Up",
  "/signup/payment": "Complete Payment",
  "/signup/complete": "Finishing Signup",
  "/players": "Prospects",
  "/duplicates": "Duplicates",
  "/trusted-contacts": "Trusted Contacts",
  "/calls": "Calls",
  "/schedule": "Schedule",
  "/schedule/build-itinerary": "Build Itinerary",
  "/video": "Video",
  "/latest-notes": "Latest Notes",
  "/recruiting-notes/new": "New Recruiting Note",
  "/rosters": "Roster Planning",
  "/rosters/roster-images": "Roster Images",
  "/settings": "Organization Settings",
  "/admin": "Admin",
  "/admin/notifications": "Notifications",
  "/admin/batch-extractions": "Batch Extractions",
  "/admin/batch-extractions/new": "New Batch Extraction",
  "/admin/roster-uploads": "Uploaded Rosters",
  "/admin/scraping": "Scraping",
  "/admin/messaging-numbers": "Messaging Numbers",
  "/admin/itinerary-requests": "Itinerary Requests",
  "/admin/sample-data": "Sample Data",
  "/admin/tours": "Tours",
  "/admin/billing": "Billing",
  "/admin/billing/settings": "Billing Settings",
  "/crm/contacts": "Contacts",
  "/crm/import": "Import",
  "/crm/communications": "Communications",
  "/crm/messages/unmatched": "Unmatched Messages",
  "/crm/twitter/followers": "Followers",
  "/crm/submissions/pending": "Pending Submissions",
  "/crm/teams": "Teams",
  "/crm/connectors": "Connectors",
  "/crm/settings/x-post-templates": "Post Templates",
};

const titleForPath = (pathname: string): string | null => {
  const normalized =
    pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  return STATIC_TITLES[normalized] ?? null;
};

export const RouteTitle = () => {
  const { pathname } = useLocation();
  const title = titleForPath(pathname);

  useEffect(() => {
    if (title) setDocumentTitle(title);
  }, [title]);

  return null;
};
